import { Flex } from '@radix-ui/themes'
import { ChevronRightIcon } from 'lucide-react'

import type { ReactNode } from 'react'

interface AnnouncementBodyProps {
  /** Names the recorded journey, e.g. "Del permiso a la planilla". */
  heading: string
  blurb: string
  /** The live caption from the journey state. */
  caption: string
  stage: ReactNode
  story: ReactNode
}

/**
 * The two-column shape every announcement shares: the looping journey on the
 * left, what it means on the right.
 *
 * Extracted from the Exportación Avanzada dialog so a second and third
 * announcement do not each re-derive the column widths, the "simulación en
 * tiempo real" marker and the caption row — and so the archive renders every
 * entry, old or new, at exactly the same proportions.
 */
export const AnnouncementBody = ({ heading, blurb, caption, stage, story }: AnnouncementBodyProps) => (
  <Flex direction={{ initial: 'column', md: 'row' }}>
    {/* Story reads first, left to right and top to bottom on mobile: what
        changed comes before the simulation of it, per stakeholder feedback. */}
    <div className="flex w-full flex-col justify-center border-b border-[#e4e1e4] p-6 md:w-[45%] md:border-b-0 md:border-r">
      {story}
    </div>

    <div className="flex w-full flex-col bg-[#faf8f8] p-6 md:w-[55%]">
      <Flex align="center" gap="2" className="mb-2">
        <span className="relative flex h-2 w-2">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#107c41] opacity-75" />
          <span className="relative inline-flex h-2 w-2 rounded-full bg-[#107c41]" />
        </span>
        <span className="font-mono text-[10px] font-bold uppercase tracking-wider text-[#107c41]">
          Simulación en tiempo real
        </span>
      </Flex>

      <p className="text-base font-bold leading-tight text-[#1c1917]">{heading}</p>
      <p className="mb-3.5 mt-1 text-xs leading-relaxed text-[#706d71]">{blurb}</p>

      <div className="flex min-h-0 flex-1 flex-col justify-center">{stage}</div>

      <Flex align="center" gap="2" className="mt-2.5 min-h-[16px]">
        <ChevronRightIcon className="h-3 w-3 shrink-0 text-[#107c41]" />
        <span className="font-mono text-[10px] text-[#706d71]">{caption}</span>
      </Flex>
    </div>
  </Flex>
)
